import React, { useMemo, useState } from 'react';
import { Search, Send, User, X } from 'lucide-react';
import ChatModalPortal from './ChatModalPortal';

const displayName = (profile) => (
  [profile.first_name, profile.last_name].filter(Boolean).join(' ')
  || 'Student'
);

export function ContactShareDialog({ open, onClose, profiles = [], onShare, isSharing = false }) {
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const filteredProfiles = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return profiles;
    return profiles.filter((profile) => (
      `${displayName(profile)} ${profile.profession || ''}`
        .toLocaleLowerCase()
        .includes(query)
    ));
  }, [profiles, search]);

  if (!open) return null;

  const share = async (profile) => {
    setError('');
    try {
      const succeeded = await onShare(profile);
      if (succeeded) {
        setSearch('');
        onClose();
      } else {
        setError('Profili nuk u nda. Provoni përsëri.');
      }
    } catch (shareError) {
      console.error('Unable to share contact card:', shareError);
      setError('Profili nuk u nda. Provoni përsëri.');
    }
  };

  return (
    <ChatModalPortal>
      <div className="fixed inset-0 z-[1000] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm">
      <section role="dialog" aria-modal="true" aria-labelledby="share-contact-title" className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border-0 bg-white shadow-2xl">
        <div aria-hidden="true" className="flex h-1.5 w-full">
          <span className="flex-1 bg-zinc-950" />
          <span className="flex-1 bg-[#DD0000]" />
          <span className="flex-1 bg-[#FFCE00]" />
        </div>
        <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
              <User size={18} />
            </span>
            <div>
              <h2 id="share-contact-title" className="text-sm font-extrabold text-zinc-950">Ndaj profil</h2>
              <p className="mt-0.5 text-xs text-zinc-500">Zgjidhni profilin që doni të ndani në bisedë.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900" aria-label="Mbyll">
            <X size={17} />
          </button>
        </header>
        <div className="border-b border-zinc-100 p-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-2.5 text-zinc-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Kërko profile..."
              className="h-9 w-full rounded-xl border border-zinc-200 bg-zinc-50 pl-9 pr-3 text-xs outline-none focus:border-amber-400 focus:bg-white"
            />
          </div>
        </div>
        {error && <p role="alert" className="px-4 pt-3 text-xs text-red-600">{error}</p>}
        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
          {filteredProfiles.length === 0 ? (
            <p className="py-8 text-center text-xs text-zinc-500">Nuk u gjet asnjë profil.</p>
          ) : filteredProfiles.map((profile) => (
            <div key={profile.id} className="flex items-center gap-3 rounded-xl p-2.5 hover:bg-amber-50/70">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-700 text-xs font-extrabold text-white">
                {profile.avatar_url ? <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" /> : displayName(profile).slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-zinc-900">{displayName(profile)}</p>
                <p className="truncate text-[11px] text-zinc-500">{profile.profession || 'Student i Gjermanishtes'}</p>
              </div>
              <button
                type="button"
                disabled={isSharing}
                onClick={() => share(profile)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-zinc-950 px-2.5 py-2 text-[11px] font-bold text-[#FFCE00] hover:bg-[#DD0000] hover:text-white disabled:opacity-50"
              >
                <Send size={12} /> Ndaj
              </button>
            </div>
          ))}
        </div>
      </section>
      </div>
    </ChatModalPortal>
  );
}

export function ContactMessage({ message, onStartChat }) {
  let contact;
  try {
    contact = JSON.parse(message.content)?.profile;
  } catch {
    return <p className="text-xs text-red-600">Profili nuk mund të shfaqet.</p>;
  }
  if (!contact?.id) return <p className="text-xs text-red-600">Të dhënat e profilit janë të pavlefshme.</p>;
  const name = displayName(contact);
  return (
    <div className="w-full min-w-[220px] max-w-sm overflow-hidden rounded-2xl border border-zinc-200 bg-white">
      <div className="flex items-center gap-3 p-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-700 text-sm font-extrabold text-white">
          {contact.avatar_url ? <img src={contact.avatar_url} alt="" className="h-full w-full object-cover" /> : name.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-extrabold text-zinc-950">{name}</p>
          <p className="truncate text-[11px] text-zinc-500">{contact.profession || 'Student i Gjermanishtes'}</p>
        </div>
      </div>
      <button type="button" onClick={() => onStartChat(contact)} className="w-full border-t border-zinc-100 bg-zinc-50 px-3 py-2 text-left text-[11px] font-bold text-[#9A6700] hover:bg-amber-50">
        Hap bisedën
      </button>
    </div>
  );
}

export default ContactShareDialog;
